import React, { useEffect, useState } from 'react';
import AdminShell from '../components/admin/AdminShell';
import { fetchAdminUsersPage } from '../services/api/admin';
import type { AdminUser } from '../types/admin';
import '../styles/register.css';
import '../styles/company-owner.css';
import '../styles/admin.css';

const AdminUsersPage: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filters, setFilters] = useState({
    query: '',
    role: '',
    enabled: '',
    locked: '',
    emailVerified: '',
    phoneVerified: '',
  });

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetchAdminUsersPage({
          ...(filters.query ? { query: filters.query.trim() } : {}),
          ...(filters.role ? { role: filters.role } : {}),
          ...(filters.enabled ? { enabled: filters.enabled === 'true' } : {}),
          ...(filters.locked ? { locked: filters.locked === 'true' } : {}),
          ...(filters.emailVerified ? { emailVerified: filters.emailVerified === 'true' } : {}),
          ...(filters.phoneVerified ? { phoneVerified: filters.phoneVerified === 'true' } : {}),
          page,
          size: 12,
          sort: 'updatedDate,desc',
        });
        if (!mounted) return;
        setUsers(response.items);
        setTotalPages(response.totalPages);
      } catch (loadError: any) {
        if (!mounted) return;
        setError(loadError.message || 'Could not load admin users.');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, [filters, page]);

  return (
    <AdminShell
      badge="Account Administration"
      copy="Inspect platform users and current account flags with backend pagination and admin filters."
      title="Admin users"
    >
      {error ? <div className="register-inline-error">{error}</div> : null}

      <section className="admin-panel">
        <div className="admin-panel-header">
          <div>
            <h2 className="admin-panel-title">Users directory</h2>
            <p className="admin-panel-copy">Search and filter admin-readable users without loading the full account list at once.</p>
          </div>
        </div>
        <div className="admin-toolbar">
          <input
            className="register-input admin-toolbar-wide"
            onChange={(event) => {
              setPage(0);
              setFilters((current) => ({ ...current, query: event.target.value }));
            }}
            placeholder="Search by name, email, username, or role"
            value={filters.query}
          />
          <select
            className="register-input"
            onChange={(event) => {
              setPage(0);
              setFilters((current) => ({ ...current, role: event.target.value }));
            }}
            value={filters.role}
          >
            <option value="">All roles</option>
            <option value="ROLE_ADMIN">Admin</option>
            <option value="ROLE_USER">Customer</option>
            <option value="ROLE_DRIVER">Driver</option>
            <option value="ROLE_COMPANY_OWNER">Company owner</option>
          </select>
          <select
            className="register-input"
            onChange={(event) => {
              setPage(0);
              setFilters((current) => ({ ...current, enabled: event.target.value }));
            }}
            value={filters.enabled}
          >
            <option value="">Enabled or disabled</option>
            <option value="true">Enabled</option>
            <option value="false">Disabled</option>
          </select>
          <select
            className="register-input"
            onChange={(event) => {
              setPage(0);
              setFilters((current) => ({ ...current, locked: event.target.value }));
            }}
            value={filters.locked}
          >
            <option value="">Locked or unlocked</option>
            <option value="true">Locked</option>
            <option value="false">Unlocked</option>
          </select>
          <select
            className="register-input"
            onChange={(event) => {
              setPage(0);
              setFilters((current) => ({ ...current, emailVerified: event.target.value }));
            }}
            value={filters.emailVerified}
          >
            <option value="">Email verification</option>
            <option value="true">Email verified</option>
            <option value="false">Email pending</option>
          </select>
          <select
            className="register-input"
            onChange={(event) => {
              setPage(0);
              setFilters((current) => ({ ...current, phoneVerified: event.target.value }));
            }}
            value={filters.phoneVerified}
          >
            <option value="">Phone verification</option>
            <option value="true">Phone verified</option>
            <option value="false">Phone pending</option>
          </select>
        </div>

        {loading ? (
          <div className="register-loader" />
        ) : (
          <>
            <div className="admin-table" style={{ marginTop: '16px' }}>
              <div className="admin-table-head admin-table-head-users">
                <span>User</span>
                <span>Role</span>
                <span>Verification</span>
                <span>Account state</span>
                <span>Notes</span>
              </div>
              {users.map((user) => (
                <div className="admin-table-row admin-table-row-users" key={user.id}>
                  <span>
                    <strong>{user.name}</strong>
                    <small>{user.email}</small>
                    <small>{user.username || 'No username'}</small>
                  </span>
                  <span>
                    <strong>{(user.roles ?? []).join(', ') || 'No roles'}</strong>
                  </span>
                  <span>
                    <strong>{user.emailVerified ? 'Email verified' : 'Email pending'}</strong>
                    <small>{user.phoneVerified ? 'Phone verified' : 'Phone pending'}</small>
                  </span>
                  <span>
                    <strong>{user.enabled ? 'Enabled' : 'Disabled'}</strong>
                    <small>{user.accountNonLocked ? 'Unlocked' : 'Locked'}</small>
                    <small>{user.credentialsNonExpired ? 'Credentials valid' : 'Credentials expired'}</small>
                  </span>
                  <span>
                    <small>ID: {user.id}</small>
                    <small>Onboarding: {user.onboardingsStep || 'Not supplied'}</small>
                  </span>
                </div>
              ))}
              {!users.length ? <p className="admin-empty">No users match the current filters.</p> : null}
            </div>
            <div className="admin-pagination">
              <button
                className="register-secondary-button"
                disabled={page === 0}
                onClick={() => setPage((current) => Math.max(current - 1, 0))}
                type="button"
              >
                Previous
              </button>
              <span>
                Page {page + 1} of {Math.max(totalPages, 1)}
              </span>
              <button
                className="register-secondary-button"
                disabled={page + 1 >= Math.max(totalPages, 1)}
                onClick={() => setPage((current) => current + 1)}
                type="button"
              >
                Next
              </button>
            </div>
            <p className="admin-muted-note" style={{ marginTop: '16px' }}>
              Role changes, lock/unlock, and enable/disable actions are intentionally not exposed yet here because the current mutation endpoints still expect internal numeric IDs instead of the UUIDs returned by the admin list contracts.
            </p>
          </>
        )}
      </section>
    </AdminShell>
  );
};

export default AdminUsersPage;
